// src/context/PlanContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

const FREE_TIER_DEFAULTS = { name: 'free', max_calendars: 1, bookings_per_month: 1000 }

// Min interval between /v1/billing/status fetches. Supabase's onAuthStateChange
// re-fires SIGNED_IN on token refresh and tab focus, which previously caused
// thousands of polls per user and exhausted the per-IP rate limit. Plan/tier
// data is stable enough that 5min staleness is invisible. Components that
// perform billing actions (upgrade, downgrade, cancel) should call
// refetchPlan() to bypass the dedupe and pick up the new tier immediately.
const PLAN_STALE_MS = 5 * 60 * 1000

const PlanContext = createContext({})

export const usePlan = () => {
  const context = useContext(PlanContext)
  if (!context) {
    throw new Error('usePlan must be used within PlanProvider')
  }
  return context
}

export const PlanProvider = ({ children }) => {
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const lastFetchRef = useRef(0)
  const { user } = useAuth()

  useEffect(() => {
    fetchPlan()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        fetchPlan()
      } else if (event === 'SIGNED_OUT') {
        setPlan(null)
        lastFetchRef.current = 0
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [user?.id])

  const fetchPlan = async () => {
    if (plan !== null && Date.now() - lastFetchRef.current < PLAN_STALE_MS) {
      return
    }
    lastFetchRef.current = Date.now()
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setLoading(false)
        return
      }

      const res = await fetch(`${API_URL}/v1/billing/status`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })

      if (res.ok) {
        const data = await res.json()
        setPlan(data.plan)
      }
    } catch (err) {
      console.error('Error fetching plan:', err)
    } finally {
      setLoading(false)
    }
  }

  const refetchPlan = () => {
    lastFetchRef.current = 0
    return fetchPlan()
  }

  const effectivePlan = plan ?? FREE_TIER_DEFAULTS
  const isPaid = effectivePlan.name !== 'free'

  return (
    <PlanContext.Provider value={{ plan: effectivePlan, loading, isPaid, refetchPlan }}>
      {children}
    </PlanContext.Provider>
  )
}
